import type React from 'react';

import type { Body, Meta, Uppy } from '@uppy/core';

import type { EndorsementFormData } from '../../shared/types';
import { EndorsementModal } from '../EndorsementModal';
import { EndorsementPreview } from '../EndorsementPreview';

import { EndorsementFormFields } from './Fields';
import { EndorsementFormFooter } from './Footer';
import { EndorsementFormHeader } from './Header';

type EndorsementFormProps = {
	title: string;
	backButtonAriaLabel: string;
	formData: EndorsementFormData;
	statementCharCount: number;
	statementMaxLength: number;
	isFormValid: boolean;
	isSubmitting: boolean;
	isHelpModalOpen: boolean;
	isSubmitModalOpen: boolean;
	onBack: () => void;
	onFieldChange: (field: keyof EndorsementFormData, value: string) => void;
	onNext: () => void;
	onHelpClick: () => void;
	onCloseHelpModal: () => void;
	onCloseSubmitModal: () => void;
	onConfirmSubmit: () => void | Promise<void>;
	pageContainer: string;
	contentContainer: string;
	formSection: string;
	previewSection: string;
	testId?: string;
	uppy: Uppy<Meta, Body>;
	image: string | undefined;
};

export const EndorsementForm: React.FC<EndorsementFormProps> = ({
	title,
	backButtonAriaLabel,
	formData,
	statementCharCount,
	statementMaxLength,
	isFormValid,
	isSubmitting,
	isHelpModalOpen,
	isSubmitModalOpen,
	onBack,
	onFieldChange,
	onNext,
	onHelpClick,
	onCloseHelpModal,
	onCloseSubmitModal,
	onConfirmSubmit,
	pageContainer,
	contentContainer,
	formSection,
	previewSection,
	testId,
	uppy,
	image,
}) => {
	return (
		<div className={pageContainer} data-testid={testId}>
			<EndorsementFormHeader onBack={onBack} title={title} backButtonAriaLabel={backButtonAriaLabel} />

			<main className={contentContainer}>
				<div className={formSection}>
					<EndorsementFormFields
						formData={formData}
						statementCharCount={statementCharCount}
						statementMaxLength={statementMaxLength}
						onFieldChange={onFieldChange}
						uppy={uppy}
						image={image}
					/>
				</div>

				<div className={previewSection}>
					<EndorsementPreview
						name={formData.name}
						title={formData.title}
						statement={formData.statement}
						photoUrl={image}
						url={formData.url}
					/>
				</div>
			</main>

			<EndorsementFormFooter
				onClick={onNext}
				onHelpClick={onHelpClick}
				isValid={isFormValid}
				data-qa="endorsement-form-footer"
			/>

			<EndorsementModal variant="help" isOpen={isHelpModalOpen} onClose={onCloseHelpModal} formData={formData} />

			<EndorsementModal
				variant="submit"
				isOpen={isSubmitModalOpen}
				isSubmitting={isSubmitting}
				onClose={onCloseSubmitModal}
				onConfirm={onConfirmSubmit}
				formData={formData}
			/>
		</div>
	);
};
